import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useKeycloakAuth, logout } from "@/hooks/useKeycloakAuth";
import { useUnifiedAuth, UnifiedUserProfile } from "@/hooks/useUnifiedAuth";
import {
  clearSilentAuthAttempt,
  login,
  loginSilently,
  markSilentAuthAttempted,
  wasSilentAuthAttempted,
} from "@/config/auth";
import {
  consumeIsExplicitLogout,
  savePostLoginRedirect,
} from "@/utils/postLoginRedirect";
import { identifyPosthogUser } from "@/lib/posthog";
import { initAnalytics } from "@/lib/analytics";
import { useAppSelector } from "@/store/hooks";
import { resolveAnalyticsGeneratorName } from "@/utils/analyticsGeneratorName";
import { useAnalyticsIdentityReady } from "@/hooks/useAnalyticsIdentityReady";
import { usePageTracking } from "@/hooks/usePageTracking";
import { setUserInSentry, clearUserInSentry } from "@/integrations/sentry/client";
import { getLandingRouteFromToken } from "@/utils/landingRoute";
import {
  getPermissionsFromToken,
  isMigratedToProfiles,
} from "@/utils/keycloakRoles";
import { SinirLoginAnnouncementModal } from "@/components/SinirLoginAnnouncementModal";
import { postToHost } from "@/embed/bridge";
import { hostRequestedRetry, isEmbedMode } from "@/embed/host";

const PERSISTED_CACHE_KEY = "MUSA_PORTAL_CACHE";

const STORAGE_KEY_PREFIX = "login_update_modal_";
const getTodayKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
};

const LoginUpdateModalContext = React.createContext<{
  openModal: () => void;
} | null>(null);
export const useLoginUpdateModalContext = () =>
  React.useContext(LoginUpdateModalContext);

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  profile: UnifiedUserProfile | null;
  signOut: () => Promise<void>;
}

/** Exportado para o guard de analytics (RN-07) poder ler `isLoading` sem lançar fora do provider. */
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

const publicPaths = [
  "/login",
  "/login/musa",
  "/login-redirect",
  "/login-redirect/musa",
  "/forgot-password",
  "/reset-password",
];

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading: authLoading } = useKeycloakAuth();
  const { profile, isLoading: profileLoading } = useUnifiedAuth();
  // geradores e unidades de GET /generators/me (os mesmos dos filtros): fonte do generator_name do GA4
  const generatorAccess = useAppSelector((s) => s.establishmentAccess);
  const navigate = useNavigate();
  const location = useLocation();

  // Combinar loading states
  const isLoading = authLoading || (isAuthenticated && profileLoading);

  // Função de logout: limpa cache (evita dados do usuário anterior ao trocar de conta)
  // Estável entre renders — quem depende dela em useMemo/useCallback não invalida.
  const signOut = useCallback(async () => {
    queryClient.clear();
    queryClient.removeQueries();
    localStorage.removeItem(PERSISTED_CACHE_KEY);
    clearUserInSentry();
    await logout(); // Limpa tokens e revoga no Keycloak
  }, [queryClient]);

  // Antes do efeito de autenticação: quando o host pede nova tentativa, a
  // trava anti-loop precisa cair — mas só aí, e só uma vez. Ver
  // hostRequestedRetry.
  useEffect(() => {
    if (hostRequestedRetry()) clearSilentAuthAttempt();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const currentPath = location.pathname;
      const isPublicPath =
        publicPaths.includes(currentPath) ||
        currentPath.startsWith("/perguntasfrequentes/live/");
      const isOAuthCallback =
        currentPath === "/login-redirect" ||
        currentPath === "/login-redirect/musa";
      if (isOAuthCallback) return;

      // Redirect unauthenticated users to Keycloak login via OAuth2.
      //
      // Primeiro tenta reautenticar em silêncio no realm do último login: aba
      // nova aberta por um link do menu nasce sem token, mas com o SSO vivo o
      // Keycloak devolve o código sem pedir senha. Só quando isso falha (ou já
      // foi tentado nesta aba) é que vai pro formulário, sempre no realm
      // `generator` — interno cuja sessão morreu não fica preso no realm
      // `musa`, e entra como interno pela rota pública /login/musa.
      //
      // O destino é guardado antes de qualquer ida ao Keycloak, para o callback
      // devolver o usuário na rota que ele clicou.
      if (!isAuthenticated && !isPublicPath) {
        if (consumeIsExplicitLogout()) {
          navigate("/", { replace: true });
          return;
        }
        savePostLoginRedirect(`${currentPath}${location.search}`);
        if (wasSilentAuthAttempted()) {
          // Embutido, o formulário do Keycloak não tem onde renderizar: o
          // realm responde `X-Frame-Options: SAMEORIGIN` e
          // `frame-ancestors 'self'`. Quem leva o usuário até lá é o host, na
          // janela de cima — o guest não pode navegar o top sem gesto do
          // usuário, e aqui não há nenhum. A reautenticação silenciosa
          // (`prompt=none`) continua funcionando dentro do iframe: ela é só
          // redirect, sem página para o navegador bloquear.
          if (isEmbedMode()) {
            postToHost({ type: "auth", state: "login_required" });
            return;
          }
          login();
        } else {
          markSilentAuthAttempted();
          loginSilently();
        }
        return;
      }

      if (
        isAuthenticated &&
        (currentPath === "/login" || currentPath === "/login/musa")
      ) {
        navigate(getLandingRouteFromToken());
        return;
      }
    }
  }, [isAuthenticated, isLoading, location.pathname, navigate]);

  const analyticsGeneratorName = profile
    ? resolveAnalyticsGeneratorName(profile, generatorAccess)
    : null;
  const isAnalyticsIdentityReady = useAnalyticsIdentityReady(profile, generatorAccess);

  useEffect(() => {
    if (!isAuthenticated || !profile?.user_id) return;

    identifyPosthogUser(profile.user_id, {
      email: profile.email,
      name: profile.user_name,
      is_internal: profile.is_internal,
    });

    setUserInSentry({
      id: profile.user_id,
      username: profile.user_name,
      email: profile.email,
    });

    // só com o nome já resolvível — senão a sessão começa com generator_name = (not set)
    if (!isAnalyticsIdentityReady) return;
    initAnalytics({
      userId: profile.user_id,
      email: profile.email,
      isInternal: profile.is_internal,
      accessType: profile.permission_role,
      generatorId: profile.generator_id,
      generatorName: analyticsGeneratorName,
    });
  }, [
    isAuthenticated,
    profile?.user_id,
    profile?.email,
    profile?.user_name,
    profile?.permission_role,
    profile?.is_internal,
    profile?.generator_id,
    analyticsGeneratorName,
    isAnalyticsIdentityReady,
  ]);

  // Permissões do usuário resolvidas na abertura do site — GR-637.
  //
  // Nada é buscado: as client roles do `portal-gerador` já vêm no token, em
  // `resource_access`, concedidas pela membership nos grupos. O log existe
  // para conferir a migração antes de a Onda 1 ligar o bloqueio de tela;
  // `migrado: false` é usuário que ainda depende do attribute `role` legado e
  // que não pode ser barrado (GR-561, regra 16).
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isAuthenticated || !profile?.user_id) return;

    console.info("[GR-637] permissões do usuário", {
      email: profile.email,
      interno: profile.is_internal,
      migrado: isMigratedToProfiles(),
      permissoes: getPermissionsFromToken(),
    });
  }, [isAuthenticated, profile?.user_id, profile?.email, profile?.is_internal]);

  // O host não enxerga o storage deste lado: sem este aviso ele não teria como
  // saber que a sessão ficou de pé e tirar o "entrando…" da frente. Só o
  // estado positivo é enviado — `login_required` sai de um ponto só, depois da
  // tentativa silenciosa falhar, para o host não navegar a janela de cima no
  // meio do round-trip do Keycloak.
  useEffect(() => {
    if (isAuthenticated) postToHost({ type: "auth", state: "authenticated" });
  }, [isAuthenticated]);

  // Logado, o page_view espera a identidade do GA4 (com o nome do gerador) ir antes dele.
  // Páginas públicas, sem login — e perfil que não carregou —, seguem registrando assim que a
  // checagem de auth termina.
  usePageTracking(!isLoading && (!isAuthenticated || !profile || isAnalyticsIdentityReady));

  // Memoizado para não invalidar consumidores a cada render do provider.
  const authValue = useMemo(
    () => ({ isAuthenticated, isLoading, profile, signOut }),
    [isAuthenticated, isLoading, profile, signOut],
  );

  return (
    <AuthContext.Provider value={authValue}>
      {isAuthenticated && !!profile && <SinirLoginAnnouncementModal />}
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
